import { subscribeToAnySignal } from 'serviceWorker/events/swSignal';
import { swSignals } from 'serviceWorker/events/swSignals';
import { isNoProjectAccessError } from 'serviceWorker/helpers/httpError';
import { isCancelledRequest } from 'shared/channels/cancellation';

/**
 * A pull that failed is not started again in the same project before this. Any
 * wait breaks the loop (nothing is cleared meanwhile, so the page reads nothing
 * again); a short one keeps a retry of the user working after a passing failure.
 */
export const PULL_RETRY_AFTER_FAILURE_MS = 5_000;
/** No access to the project: asking again changes nothing until the permissions change (they end the wait). */
export const PULL_RETRY_AFTER_NO_PROJECT_ACCESS_MS = 10 * 60_000;

export interface PullFailure {
  projectId: string | undefined;
  failedAt: number;
  error: unknown;
}

export interface PullFailureCooldown {
  /** A cancelled pull (logout, project switch) failed at nothing: it is not remembered. */
  remember: (projectId: string | undefined, error: unknown) => void;
  forget: () => void;
  /** The recent failure a new pull of the project would only repeat; `undefined` when it may run. */
  failureToRepeat: (projectId: string | undefined) => PullFailure | undefined;
}

function cooldownOf(error: unknown): number {
  return isNoProjectAccessError(error) ? PULL_RETRY_AFTER_NO_PROJECT_ACCESS_MS : PULL_RETRY_AFTER_FAILURE_MS;
}

/**
 * Keeps a failed pull from being started again at once. A pull clears its stored
 * collection first, which tells the page its data changed; the page reads it again,
 * the read starts the pull, and a failure that persists (no access to the project,
 * a server error) turned into a loop of requests and error logs. Stored permissions
 * (the access to the project may have changed), a logout (another user may log in)
 * and the network coming back end the wait; it listens to them only while a failure
 * is remembered.
 */
export function createPullFailureCooldown(now: () => number = Date.now): PullFailureCooldown {
  let lastFailure: PullFailure | undefined;
  let stopListening: (() => void) | undefined;

  const forget = (): void => {
    lastFailure = undefined;
    stopListening?.();
    stopListening = undefined;
  };

  return {
    remember: (projectId, error) => {
      if (isCancelledRequest(error)) {
        return;
      }
      lastFailure = { projectId, failedAt: now(), error };
      stopListening ??= subscribeToAnySignal(
        [swSignals.permissionsStored, swSignals.loggedOut, swSignals.networkRestored],
        forget
      );
    },
    forget,
    failureToRepeat: (projectId) => {
      if (!lastFailure || lastFailure.projectId !== projectId) {
        return undefined;
      }
      return now() - lastFailure.failedAt < cooldownOf(lastFailure.error) ? lastFailure : undefined;
    },
  };
}
